import React, { useEffect, useState, useRef, useMemo } from 'react';
import dayjs, { Dayjs } from '../utils/dayjsConfig';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Picker from '@emoji-mart/react';
import {
  createMessageScheduling,
  deleteMessageScheduling,
  getMessageSchedulings,
  updateMessageScheduling,
  getMessageScheduling
} from '../services/MessageSchedulingsService';
import { getTags, Tag } from '../services/LabelService';
import { getContacts, Contact } from '../services/ContactService';
import SessionService from '../services/SessionService';
import './MessageSchedule.css';
import Toast, { ToastContainer } from '../components/Toast';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import { createAppDate, formatInAppTimezone, APP_TIMEZONE } from '../utils/dayjsConfig';

// Configurar plugins do dayjs
dayjs.extend(customParseFormat);

// Usar as funções utilitárias centralizadas de timezone

// Definindo explicitamente a interface CreateMessageSchedulingDTO para corrigir o erro de tipo
interface CreateMessageSchedulingDTO {
  name: string;
  messageText: string;
  sendDate: string;
  contactId: number;
  sectorId: number;
  status: boolean;
  tagIds: string;
  attachments: {
    type: 'image' | 'audio' | 'file';
    data: string;
    name: string;
    mimeType: string;
  }[];
}

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

const google = window.google;
const gapi = window.gapi;

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result.split(',')[1]);
      } else {
        reject('Failed to convert blob to base64');
      }
    };
    reader.onerror = error => reject(error);
  });
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result.split(',')[1]);
      } else {
        reject('Failed to convert file to base64');
      }
    };
    reader.onerror = error => reject(error);
  });
};

const Icons = {
  Plus: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  ),
  Calendar: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  Clock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  Edit: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  ),
  Delete: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18"></path>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  ),
  Paperclip: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>
  ),
  Picture: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  ),
  Mic: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
      <line x1="12" y1="19" x2="12" y2="23"></line>
      <line x1="8" y1="23" x2="16" y2="23"></line>
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
  ChevronRight: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
  ),
};

interface Message {
  id?: number;
  title: string;
  date: number | null;
  description: string;
  labels: string[];
  contactId: number | null;
  contact: Contact;
  attachments?: Array<{
    id: number;
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
    messageId: number;
    createdAt: string;
  }>;
}

const SCOPES = 'https://www.googleapis.com/auth/calendar';

interface MessageScheduleProps {
  visible: boolean;
  onClose: () => void;
  onSave: () => void;
  contactId: number;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface BackendAttachment {
  type: string;
  data: string;
  name: string;
  preview?: string;
  duration?: number;
}

interface Attachment {
  fileType: 'image' | 'audio' | 'file';
  data: string;
  name: string;
  preview?: string;
  duration?: number;
  mimeType: string;
  s3Url?: string;
}

// Componente de calendário customizado - movido para fora do escopo principal
const CustomCalendar: React.FC<{
  selectedDate: dayjs.Dayjs | null;
  onChange: (date: dayjs.Dayjs) => void;
  onClose?: () => void;
}> = ({ selectedDate, onChange }) => {
  const localNow = createAppDate();
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.month() : localNow.month()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.year() : localNow.year()
  );
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const daysInMonth = createAppDate().year(currentYear).month(currentMonth).daysInMonth();
  const firstDayOfMonth = createAppDate().year(currentYear).month(currentMonth).startOf('month').day();

  const daysGrid = Array.from({ length: 42 }, (_, i) => {
    const adjustedIndex = i - firstDayOfMonth + 1;
    return adjustedIndex > 0 && adjustedIndex <= daysInMonth ? adjustedIndex : null;
  });

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    if (day !== null) {
      const newDate = createAppDate().year(currentYear).month(currentMonth).date(day);
      onChange(newDate);
    }
  };

  const isToday = (day: number): boolean => {
    const today = createAppDate();
    return (
      day === today.date() &&
      currentMonth === today.month() &&
      currentYear === today.year()
    );
  };

  const isSelected = (day: number): boolean => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.date() &&
      currentMonth === selectedDate.month() &&
      currentYear === selectedDate.year()
    );
  };

  const monthNames = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  return (
    <div className="custom-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-button" onClick={prevMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="calendar-month-year">
          {monthNames[currentMonth]} {currentYear}
        </div>
        <button className="calendar-nav-button" onClick={nextMonth}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <div className="calendar-weekdays">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, index) => (
          <div key={index} className="calendar-weekday">{day}</div>
        ))}
      </div>

      <div className="calendar-days">
        {daysGrid.map((day, index) => (
          <div
            key={index}
            className={`calendar-day ${day === null ? 'empty' : ''} ${day !== null && isToday(day) ? 'today' : ''
              } ${day !== null && isSelected(day) ? 'selected' : ''} ${day !== null && hoveredDay === day && !isSelected(day) ? 'hovered' : ''
              }`}
            onClick={() => day !== null && handleDateClick(day)}
            onMouseEnter={() => day !== null && setHoveredDay(day)}
            onMouseLeave={() => setHoveredDay(null)}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente de seleção de hora - movido para fora do escopo principal
const TimeSelector: React.FC<{
  selectedTime: dayjs.Dayjs | null;
  onChange: (time: dayjs.Dayjs) => void;
  onClose?: () => void;
}> = ({ selectedTime, onChange, onClose }) => {
  const hoursArray = Array.from({ length: 24 }, (_, i) => i);
  const minutesArray = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const localNow = createAppDate();
  const [hours, setHours] = useState<number>(selectedTime ? selectedTime.hour() : localNow.hour());
  const [minutes, setMinutes] = useState<number>(
    selectedTime ?
      selectedTime.minute() - (selectedTime.minute() % 5) :
      Math.floor(localNow.minute() / 5) * 5
  );

  useEffect(() => {
    // Atualiza automaticamente o tempo selecionado quando os valores de hora e minuto mudam
    const newTime = createAppDate().hour(hours).minute(minutes);
    onChange(newTime);
  }, [hours, minutes, onChange]);

  const handleHourClick = (hour: number) => {
    setHours(hour);
  };

  const handleMinuteClick = (minute: number) => {
    setMinutes(minute);
  };

  return (
    <div className="time-selector">
      <div className="time-selector-header">
        <div className="selected-time">
          {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
        </div>
      </div>

      <div className="time-selector-body">
        <div className="time-selector-column">
          <div className="time-selector-label">Horas</div>
          <div className="time-selector-items hours">
            {hoursArray.map((hour) => (
              <div
                key={hour}
                className={`time-item ${hours === hour ? 'selected' : ''}`}
                onClick={() => handleHourClick(hour)}
              >
                {String(hour).padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>

        <div className="time-selector-column">
          <div className="time-selector-label">Minutos</div>
          <div className="time-selector-items minutes">
            {minutesArray.map((minute) => (
              <div
                key={minute}
                className={`time-item ${minutes === minute ? 'selected' : ''}`}
                onClick={() => handleMinuteClick(minute)}
              >
                {String(minute).padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MessageSchedule: React.FC<MessageScheduleProps & {
  initialData?: {
    id?: number
    name?: string;
    messageText: string;
    selectedDate: dayjs.Dayjs | null;
    selectedTime: dayjs.Dayjs | null;
    selectedContactId: number | null;
    attachments: Attachment[];
  }
}> = ({
  visible,
  onClose,
  onSave,
  contactId,
  addToast,
  initialData
}) => {
    const [messageName, setMessageName] = useState<string>(initialData?.name || '');
    const [messageText, setMessageText] = useState<string>(initialData?.messageText || '');
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(initialData?.selectedDate || null);
    const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | null>(initialData?.selectedTime || null);
    const [selectedContactId, setSelectedContactId] = useState<number | null>(initialData?.selectedContactId || contactId);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
    const [contactSearchTerm, setContactSearchTerm] = useState<string>('');
    const [showContactDropdown, setShowContactDropdown] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [attachments, setAttachments] = useState<Attachment[]>(initialData?.attachments || []);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);

    // Refs para os inputs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    // Estados para gravação de áudio
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [recordingTime, setRecordingTime] = useState<number>(0);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);

    // Estados para dropdowns
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    const [showTimeSelector, setShowTimeSelector] = useState<boolean>(false);
    const calendarRef = useRef<HTMLDivElement>(null);
    const timeSelectorRef = useRef<HTMLDivElement>(null);

    // Novo estado para modal unificado de data e hora
    const [showDateTimeModal, setShowDateTimeModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');

    // Função para adicionar toasts
    const addLocalToast = (message: string, type: 'success' | 'error' | 'info') => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, message, type }]);

      // Auto remove toast after 3 seconds
      setTimeout(() => {
        removeToast(id);
      }, 3000);
    };

    const removeToast = (id: number) => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    // Buscar contatos
    useEffect(() => {
      const fetchContacts = async () => {
        try {
          setIsLoading(true);
          const token = SessionService.getToken();
          const sectorId = SessionService.getSectorId();

          if (!token || !sectorId) {
            return;
          }

          const fetchedContacts = await getContacts(sectorId);

          let contactsData: Contact[] = [];

          if (Array.isArray(fetchedContacts)) {
            contactsData = fetchedContacts;
          } else if (fetchedContacts && 'data' in fetchedContacts) {
            contactsData = fetchedContacts.data;
          }

          // Ordenar contatos por nome
          const sortedContacts = contactsData.sort((a, b) =>
            a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
          );

          setContacts(sortedContacts);
          setFilteredContacts(sortedContacts);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      };

      if (visible) {
        fetchContacts();
      }
    }, [visible]);

    // Filtrar contatos baseado no termo de pesquisa
    useEffect(() => {
      if (!contactSearchTerm.trim()) {
        setFilteredContacts(contacts);
      } else {
        const filtered = contacts.filter(contact =>
          contact.name.toLowerCase().includes(contactSearchTerm.toLowerCase())
        );
        setFilteredContacts(filtered);
      }
    }, [contactSearchTerm, contacts]);

    // Fechar dropdowns quando clicar fora
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          calendarRef.current &&
          !calendarRef.current.contains(event.target as Node) &&
          showCalendar
        ) {
          setShowCalendar(false);
        }

        if (
          timeSelectorRef.current &&
          !timeSelectorRef.current.contains(event.target as Node) &&
          showTimeSelector
        ) {
          setShowTimeSelector(false);
        }

        // Fechar dropdown de contatos quando clicar fora
        const contactDropdown = document.querySelector('.custom-select');
        if (
          contactDropdown &&
          !contactDropdown.contains(event.target as Node) &&
          showContactDropdown
        ) {
          setShowContactDropdown(false);
          setContactSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showCalendar, showTimeSelector, showContactDropdown]);

    // Timer para gravação de áudio
    useEffect(() => {
      let interval: NodeJS.Timeout;

      if (isRecording) {
        interval = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }, [isRecording]);

    // Iniciar gravação de áudio
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const audioChunks: BlobPart[] = [];

        recorder.ondataavailable = e => {
          audioChunks.push(e.data);
        };

        recorder.onstop = () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/mpeg' });
          setAudioBlob(audioBlob);

          // Encerrar stream
          stream.getTracks().forEach(track => track.stop());
        };

        setMediaRecorder(recorder);
        recorder.start();
        setIsRecording(true);
        setRecordingTime(0);
      } catch (error) {
        addLocalToast('Erro ao acessar microfone', 'error');
      }
    };

    // Parar gravação de áudio
    const stopRecording = () => {
      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
        setIsRecording(false);
      }
    };

    // Iniciar ou parar gravação
    const handleAudioRecording = () => {
      if (isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    };

    // Manipular seleção de arquivos
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'document') => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      try {
        const file = files[0];
        const base64 = await fileToBase64(file);

        if (type === 'image') {
          setAttachments(prev => [
            ...prev,
            {
              fileType: 'image',
              data: base64,
              name: file.name,
              mimeType: file.type
            }
          ]);
        } else {
          setAttachments(prev => [
            ...prev,
            {
              fileType: 'file',
              data: base64,
              name: file.name,
              mimeType: file.type
            }
          ]);
        }
      } catch (error) {
        addLocalToast('Erro ao processar arquivo', 'error');
      }

      event.target.value = '';
    };

    // Salvar áudio gravado
    const handleSaveAudio = async () => {
      if (!audioBlob) return;

      try {
        const base64 = await blobToBase64(audioBlob);

        setAttachments(prev => [
          ...prev,
          {
            fileType: 'audio',
            data: base64,
            name: `audio_${new Date().toISOString()}.mp3`,
            duration: recordingTime,
            mimeType: 'audio/mpeg'
          }
        ]);

        setAudioBlob(null);
        setRecordingTime(0);
      } catch (error) {
        addLocalToast('Erro ao salvar áudio', 'error');
      }
    };

    // Remover anexo
    const removeAttachment = (index: number) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Renderizar preview de anexos
    const renderAttachmentsPreview = () => {
      if (attachments.length === 0) {
        return (
          <div className="no-attachments">
            Nenhum anexo adicionado
          </div>
        );
      }

      return (
        <div className="attachment-list">
          {attachments.map((attachment, index) => {
            if (attachment.fileType === 'image') {
              return (
                <div key={index} className="attachment-item">
                  <img
                    src={attachment.s3Url || `data:${attachment.mimeType};base64,${attachment.data}`}
                    alt={attachment.name}
                  />
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            } else if (attachment.fileType === 'audio') {
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                      <line x1="12" y1="19" x2="12" y2="22"></line>
                    </svg>
                    <span>
                      {attachment.name.substring(0, 25)}
                      {attachment.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  {attachment.s3Url && (
                    <audio
                      ref={audioRef}
                      src={attachment.s3Url}
                      style={{ display: 'none' }}
                    />
                  )}
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            } else {
              return (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                    <span>
                      {attachment.name.substring(0, 25)}
                      {attachment.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              );
            }
          })}
        </div>
      );
    };

    // Salvar agendamento
    const handleSave = async () => {
      try {
        if (!selectedDate || !selectedTime) {
          addLocalToast('Selecione data e hora para o agendamento', 'error');
          return;
        }
        if (!messageName.trim()) {
          addLocalToast('Digite um título para o agendamento', 'error');
          return;
        }
        if (!messageText.trim() && attachments.length === 0) {
          addLocalToast('Adicione uma mensagem ou anexo', 'error');
          return;
        }
        if (attachments.length > 0 && !messageText.trim()) {
          addLocalToast('Adicione uma mensagem para acompanhar o anexo', 'error');
          return;
        }
        if (contacts.length === 0) {
          addLocalToast('Não há contatos disponíveis neste setor', 'error');
          return;
        }
        if (!selectedContactId) {
          addLocalToast('Selecione um contato', 'error');
          return;
        }
        setIsSaving(true);

        // Formatar a data e hora visualmente selecionadas
        const dateStr = selectedDate.format('YYYY-MM-DD');
        const timeStr = selectedTime.format('HH:mm');

        // Criar a data combinada interpretando-a explicitamente como horário de Brasília
        const combinedDate = dayjs.tz(`${dateStr} ${timeStr}`, 'America/Sao_Paulo');

        const token = SessionService.getToken();
        const sectorId = SessionService.getSectorId();
        if (!token || !sectorId) {
          addLocalToast('Erro de autenticação', 'error');
          setIsSaving(false);
          return;
        }

        // Enviar a data/hora no formato ISO local (sem conversão para UTC)
        // Isso garante que se o usuário seleciona 12:00, será salvo 12:00 no banco
        const sendDateFormatted = combinedDate.format('YYYY-MM-DDTHH:mm:ss');

        const dto: CreateMessageSchedulingDTO = {
          name: messageName.trim(),
          messageText,
          sendDate: sendDateFormatted,
          contactId: selectedContactId,
          sectorId: Number(sectorId),
          status: true,
          tagIds: '',
          attachments: attachments.map(att => ({
            type: att.fileType,
            data: att.data,
            name: att.name,
            mimeType: att.mimeType
          }))
        };


        if (initialData?.id) {
          await updateMessageScheduling(initialData.id, dto);
          addToast('Agendamento atualizado com sucesso', 'success');
        } else {
          await createMessageScheduling(dto);
          addToast('Mensagem agendada com sucesso', 'success');
        }
        setMessageName('');
        setMessageText('');
        setSelectedDate(null);
        setSelectedTime(null);
        setAttachments([]);
        setContactSearchTerm('');
        setShowContactDropdown(false);
        onSave();
        onClose();
      } catch (error) {
        addLocalToast('Erro ao agendar mensagem', 'error');
      } finally {
        setIsSaving(false);
      }
    };

    // Manipular mudança de data
    const handleDateChange = (date: dayjs.Dayjs) => {
      setSelectedDate(date);
    };

    // Manipular mudança de hora
    const handleTimeChange = (time: dayjs.Dayjs) => {
      setSelectedTime(time);
    };

    // Abrir modal unificado de data e hora
    const openDateTimeModal = (initialTab: 'date' | 'time' = 'date') => {
      setShowDateTimeModal(true);
      setActiveTab(initialTab);
    };

    // Mudar para a aba de hora
    const switchToTimeTab = () => {
      setActiveTab('time');
    };

    // Fechar o modal de data/hora
    const closeDateTimeModal = () => {
      setShowDateTimeModal(false);
    };

    // Renderizar o drawer
    const renderDrawer = () => {
      if (!visible) return null;

      return (
        <div className="drawer ms-drawer">
          <div className="drawer-content ms-drawer-content">
            <div className="drawer-header ms-drawer-header">
              <h2>Agendar Mensagem</h2>
              <button className="close-button" onClick={() => {
                setContactSearchTerm('');
                setShowContactDropdown(false);
                onClose();
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="drawer-body ms-drawer-body">
              <div className="form-group">
                <label>Data e Hora</label>
                <div className="datetime-inputs">
                  <div className="date-picker-container">
                    <div
                      className="date-input-wrapper"
                      onClick={() => openDateTimeModal('date')}
                    >
                      <div className="date-input-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <span className="date-input">
                        {selectedDate ? selectedDate.format('DD/MM/YYYY') : 'Selecionar data'}
                      </span>
                    </div>
                  </div>

                  <div className="time-picker-container">
                    <div
                      className="time-input-wrapper"
                      onClick={() => openDateTimeModal('time')}
                    >
                      <div className="time-input-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                      <span className="time-input">
                        {selectedTime ? selectedTime.format('HH:mm') : 'Selecionar hora'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Título do Agendamento</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Digite um título para o agendamento..."
                  value={messageName}
                  onChange={(e) => setMessageName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Contato</label>
                <div className="contact-select-container">
                  <div className="custom-select">
                    <div
                      className="custom-select-header"
                      onClick={() => setShowContactDropdown(!showContactDropdown)}
                    >
                      <span className="custom-select-value">
                        {selectedContactId ?
                          contacts.find(c => c.id === selectedContactId)?.name || 'Selecione um contato' :
                          'Selecione um contato'
                        }
                      </span>
                      <svg
                        className={`custom-select-arrow ${showContactDropdown ? 'open' : ''}`}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>

                    {showContactDropdown && (
                      <div className="custom-select-dropdown">
                        <div className="custom-select-search">
                          <input
                            type="text"
                            placeholder="Pesquisar contatos..."
                            value={contactSearchTerm}
                            onChange={(e) => setContactSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>

                        <div className="custom-select-options">
                          {filteredContacts.length > 0 ? (
                            filteredContacts.map(contact => (
                              <div
                                key={contact.id}
                                className={`custom-select-option ${selectedContactId === contact.id ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedContactId(contact.id);
                                  setShowContactDropdown(false);
                                  setContactSearchTerm('');
                                }}
                              >
                                {contact.name}
                              </div>
                            ))
                          ) : (
                            <div className="custom-select-no-results">
                              {contactSearchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato disponível'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Mensagem</label>
                <textarea
                  className="message-textarea"
                  placeholder="Digite sua mensagem..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Anexos</label>
                <div className="attachment-tools ms-attachment-tools">


                  <button
                    type="button"
                    className="tool-button ms-tool-button"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    Imagem
                  </button>

                  <button
                    type="button"
                    className="tool-button ms-tool-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                    Documento
                  </button>


                </div>

                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, 'image')}
                />

                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, 'document')}
                />

                {renderAttachmentsPreview()}
              </div>
            </div>

            <div className="drawer-footer">
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Salvando...' : 'Agendar'}
              </button>
            </div>
          </div>

          {/* Modal unificado para data e hora */}
          {showDateTimeModal && (
            <div className="datetime-overlay" onClick={closeDateTimeModal}>
              <div className="unified-datetime-modal" onClick={(e) => e.stopPropagation()}>
                <div className="unified-datetime-header">
                  <h3 className="unified-datetime-title">
                    {activeTab === 'date' ? 'Selecionar Data' : 'Selecionar Horário'}
                  </h3>
                  <button
                    className="unified-datetime-close"
                    onClick={closeDateTimeModal}
                  >
                    ×
                  </button>
                </div>

                <div className="unified-datetime-tabs">
                  <button
                    className={`unified-datetime-tab ${activeTab === 'date' ? 'active' : ''}`}
                    onClick={() => setActiveTab('date')}
                  >
                    Data
                  </button>
                  <button
                    className={`unified-datetime-tab ${activeTab === 'time' ? 'active' : ''}`}
                    onClick={() => setActiveTab('time')}
                  >
                    Horário
                  </button>
                </div>

                <div className="unified-datetime-content">
                  {activeTab === 'date' && (
                    <CustomCalendar
                      selectedDate={selectedDate}
                      onChange={(date) => {
                        handleDateChange(date);
                        // Troca automaticamente para a aba de hora após selecionar a data
                        setActiveTab('time');
                      }}
                    />
                  )}

                  {activeTab === 'time' && (
                    <TimeSelector
                      selectedTime={selectedTime || createAppDate()}
                      onChange={handleTimeChange}
                    />
                  )}
                </div>

                <div className="unified-datetime-actions">
                  <button
                    className="unified-datetime-cancel"
                    onClick={closeDateTimeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    className="unified-datetime-confirm"
                    onClick={closeDateTimeModal}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer>
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                onClose={() => removeToast(toast.id)}
              />
            ))}
          </ToastContainer>
        </div>
      );
    };

    return renderDrawer();
  };

const MessageScheduleScreen: React.FC = () => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [flows, setFlows] = useState([]);
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState<Message>({
    title: '',
    date: null,
    description: '',
    labels: [],
    contactId: null,
    contact: {} as Contact
  });
  const [dateInputValue, setDateInputValue] = useState('');
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);
  const [isSavingAudio, setIsSavingAudio] = useState(false);
  const [drawerInitialData, setDrawerInitialData] = useState<any>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const ITEMS_PER_PAGE = 10;
  const navigate = useNavigate();

  const filteredMessages = useMemo(() => {
    if (!searchFilter.trim()) return messages;
    const q = searchFilter.toLowerCase();
    return messages.filter(m =>
      (m.title || '').toLowerCase().includes(q) ||
      (m.contact?.name || '').toLowerCase().includes(q) ||
      (m.description || '').toLowerCase().includes(q)
    );
  }, [messages, searchFilter]);

  const paginatedMessages = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredMessages.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMessages, currentPage]);

  const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE);

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(current => [...current, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const renderDrawer = () => {
    if (!isDrawerVisible) return null;
    return (
      <MessageSchedule
        visible={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        onSave={() => {
          setIsDrawerVisible(false);
          fetchInitialData();
        }}
        contactId={newMessage.contactId || 0}
        addToast={addToast}
        initialData={drawerInitialData}
      />
    );
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      setMediaRecorder(recorder);
      audioChunks.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        const url = URL.createObjectURL(audioBlob);
        setRecordedAudio(audioBlob);
        setAudioURL(url);
      };

      recorder.start();
      setIsRecording(true);
    } catch (error) {
      addToast('Erro ao iniciar gravação', 'error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const handleAudioRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'document') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const base64Data = await fileToBase64(file);
      const newAttachment: Attachment = {
        fileType: type === 'document' ? 'file' : type,
        data: base64Data,
        name: file.name,
        preview: type === 'image' ? URL.createObjectURL(file) : undefined,
        mimeType: file.type
      };

      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      addToast('Erro ao processar arquivo', 'error');
    }
  };

  const handleSaveAudio = async () => {
    if (!recordedAudio) return;

    try {
      setIsSavingAudio(true);
      const base64Audio = await blobToBase64(recordedAudio);

      // Determinar extensão baseada no tipo MIME
      let extension = 'webm';
      if (recordedAudio.type.includes('wav')) extension = 'wav';
      else if (recordedAudio.type.includes('mp4') || recordedAudio.type.includes('aac')) extension = 'm4a';
      else if (recordedAudio.type.includes('ogg')) extension = 'ogg';
      else if (recordedAudio.type.includes('mpeg')) extension = 'mp3';

      const newAttachment: Attachment = {
        fileType: 'audio',
        data: base64Audio,
        name: `recorded_audio_${Date.now()}.${extension}`,
        mimeType: recordedAudio.type || 'audio/webm'
      };

      setAttachments(prev => [...prev, newAttachment]);
      setRecordedAudio(null);
      setAudioURL(null);
    } catch (error) {
      addToast('Erro ao salvar áudio', 'error');
    } finally {
      setIsSavingAudio(false);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const renderAttachmentsPreview = () => {
    if (!attachments.length) {
      return <div className="no-attachments">Nenhum anexo adicionado</div>;
    }

    const images = attachments.filter(att => att.fileType === 'image');
    const audios = attachments.filter(att => att.fileType === 'audio');
    const documents = attachments.filter(att => att.fileType === 'file');

    return (
      <div className="attachments-preview">
        {images.length > 0 && (
          <div className="attachment-section">
            <h4>Imagens ({images.length})</h4>
            <div className="attachment-list">
              {images.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Picture />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {audios.length > 0 && (
          <div className="attachment-section">
            <h4>Áudios ({audios.length})</h4>
            <div className="attachment-list">
              {audios.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Mic />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {documents.length > 0 && (
          <div className="attachment-section">
            <h4>Documentos ({documents.length})</h4>
            <div className="attachment-list">
              {documents.map((att, index) => (
                <div key={index} className="attachment-item">
                  <div className="attachment-item-content">
                    <Icons.Paperclip />
                    <span className="attachment-name">
                      {att.name.substring(0, 25)}
                      {att.name.length > 25 ? '...' : ''}
                    </span>
                  </div>
                  <button onClick={() => removeAttachment(attachments.indexOf(att))} className="remove-attachment">
                    <Icons.Delete />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const sectorId = SessionService.getSectorId();
      setSelectedSector(sectorId);

      if (!sectorId) {
        setMessages([]);
        setTags([]);
        setContacts([]);
        return;
      }


      // Buscar tags e contatos primeiro
      const [tagsResponse, contactsResponse] = await Promise.all([
        getTags(sectorId).catch(error => {
          return { data: [] };
        }),
        getContacts(sectorId).catch(error => {
          return { data: [] };
        })
      ]);

      // Definir tags e contatos

      if (Array.isArray(tagsResponse.data)) {
        setTags(tagsResponse.data);
      } else {
        setTags([]);
      }

      if (Array.isArray(contactsResponse.data)) {
        setContacts(contactsResponse.data);
      } else {
        setContacts([]);
      }

      // Buscar mensagens agendadas
      try {
        const messagesResponse = await getMessageSchedulings();

        // Se tiver mensagens, mapear para o formato do componente
        if (Array.isArray(messagesResponse)) {

          const formattedMessages = messagesResponse.map(msg => {
            const messageContact = contactsResponse.data?.find(c => c.id === (msg.contactId || msg.contact_id));

            // Log para depuração
            // Parse da data ISO interpretando-a literalmente como horário de Brasília
            const dateObj = dayjs.tz(msg.scheduledDate || msg.sendDate, 'America/Sao_Paulo');

            return {
              id: msg.id,
              title: msg.name || "Sem título",
              date: dateObj.isValid() ? dateObj.valueOf() : null,
              description: msg.messageText || msg.content || "",
              labels: msg.tagIds ? msg.tagIds.split(',') : [],
              contactId: msg.contactId || msg.contact_id || null,
              contact: messageContact || (contactsResponse.data?.[0] || {}),
              attachments: msg.attachments || []
            };
          });

          setMessages(formattedMessages);
        } else {
          setMessages([]);
        }
      } catch (error: any) {
        // Se for 404, significa que não há mensagens ainda
        if (error?.response?.status === 404) {
          setMessages([]);
        } else {
          // Se for outro erro, logar e mostrar array vazio
          addToast('Erro ao carregar mensagens agendadas', 'error');
          setMessages([]);
        }
      }
    } catch (error) {
      addToast('Erro ao carregar dados', 'error');
      setMessages([]);
      setTags([]);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();

    const handleSectorChange = () => {
      fetchInitialData();
    };

    window.addEventListener('sectorChanged', handleSectorChange);

    return () => {
      window.removeEventListener('sectorChanged', handleSectorChange);
    };
  }, []);

  const validateForm = () => {
    const newErrors: { [key: string]: string | null } = {};

    if (!newMessage.title.trim()) {
      newErrors.title = null;
    }

    if (!newMessage.date) {
      newErrors.date = null;
    }

    if (!newMessage.description.trim()) {
      newErrors.description = null;
    }

    if (!newMessage.contactId) {
      newErrors.contactId = null;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = async (messageId: number) => {
    if (!messageId) return;

    setIsLoading(true);
    try {
      await deleteMessageScheduling(messageId);
      addToast('Mensagem excluída com sucesso', 'success');
      await fetchInitialData();
    } catch (error: any) {
      addToast(error.response?.data?.message || 'Erro ao excluir mensagem', 'error');
    } finally {
      setConfirmDeleteId(null);
      setIsLoading(false);
    }
  };

  const showDrawer = async (message: Message | null = null) => {
    if (message !== null) {
      const msgIndex = messages.findIndex(m => m.id === message.id);
      setCurrentMessageIndex(msgIndex >= 0 ? msgIndex : 0);
      setIsLoading(true);
      try {
        const data = await getMessageScheduling(message.id!);

        // Parse da data ISO interpretando-a literalmente como horário de Brasília
        const dateObj = dayjs.tz(data.scheduledDate || data.sendDate, 'America/Sao_Paulo');
        setNewMessage({
          id: data.id,
          title: data.name,
          date: dateObj.isValid() ? dateObj.valueOf() : null,
          description: data.messageText || data.content || '',
          labels: data.tagIds ? data.tagIds.split(',') : [],
          contactId: data.contactId ?? data.contact_id ?? null,
          contact: contacts.find(c => c.id === (data.contactId ?? data.contact_id)) || {} as Contact,
        });

        // Mapear anexos para o formato esperado pelo componente
        const mappedAttachments = (data.attachments || []).map((att: any): Attachment => ({
          fileType: (att.fileType === 'image' ? 'image' : att.fileType === 'audio' ? 'audio' : 'file') as 'image' | 'audio' | 'file',
          data: att.fileUrl || '',   // Usar a URL do arquivo
          name: att.fileName,
          preview: att.fileUrl,      // Usar a URL como preview
          s3Url: att.fileUrl,        // Manter a URL original
          mimeType:
            att.fileType === 'image' ? (att.fileName.endsWith('.png') ? 'image/png' : 'image/jpeg') :
              att.fileType === 'audio' ? 'audio/mpeg' :
                att.fileName.endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream'
        }));

        setAttachments(mappedAttachments);
        setIsDrawerVisible(true);

        // Configurar os dados iniciais para o drawer
        setDrawerInitialData({
          id: data.id,
          name: data.name,
          messageText: data.messageText || data.content || '',
          selectedDate: dateObj,
          selectedTime: dateObj,
          selectedContactId: data.contactId ?? data.contact_id,
          attachments: mappedAttachments
        });
      } catch (error) {
        addToast('Erro ao carregar dados do agendamento', 'error');

        setNewMessage({
          id: message.id,
          title: message.title,
          date: message.date,
          description: message.description,
          labels: message.labels,
          contactId: message.contactId,
          contact: message.contact,
        });
        setAttachments([]);
        setDrawerInitialData(undefined);
        setIsDrawerVisible(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setNewMessage({
        title: '',
        date: null,
        description: '',
        labels: [],
        contactId: null,
        contact: {} as Contact
      });
      setAttachments([]);
      setDrawerInitialData(undefined);
      setIsDrawerVisible(true);
    }
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateInputValue(value);

    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setNewMessage({ ...newMessage, date: date.getTime() });
        if (errors.date !== undefined) {
          setErrors({ ...errors, date: null });
        }
      }
    } else {
      setNewMessage({ ...newMessage, date: null });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage({
      ...newMessage,
      description: e.target.value,
    });
  };

  const removeTag = () => {
    setNewMessage(prev => ({
      ...prev,
      labels: []
    }));
  };

  const toggleTagSelection = (tagId: string) => {
    setNewMessage(prev => ({
      ...prev,
      labels: [tagId]
    }));
    setIsTagsDropdownOpen(false);
  };

  const getAttachmentCounts = (message: Message) => {
    const atts = message.attachments || [];
    const images = atts.filter((att: { fileType?: string }) => att.fileType === 'image' || att.fileType?.includes?.('image')).length;
    const audio = atts.filter((att: { fileType?: string }) => att.fileType === 'audio' || att.fileType?.includes?.('audio')).length;
    const files = atts.filter((att: { fileType?: string }) =>
      att.fileType === 'file' || (!att.fileType?.includes?.('image') && !att.fileType?.includes?.('audio'))
    ).length;
    return { images, audio, files };
  };

  return (
    <div className="message-schedule-screen">
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="message-schedule-header">
        <div className="message-schedule-header-content">
          <h1>Agendamento de mensagem</h1>
          <p className="message-schedule-header-description">
            Gerencie suas mensagens agendadas
          </p>
        </div>
        {selectedSector && (
          <button className="message-schedule-add-button" onClick={() => showDrawer(null)}>
            <Icons.Plus />
            Nova mensagem
          </button>
        )}
      </div>

      <div className="message-schedule-content">
        {!selectedSector ? (
          <div className="no-sector-text">
            Nenhum setor selecionado
          </div>
        ) : messages.length === 0 ? (
          <div className="message-schedule-empty">
            <Icons.Calendar />
            <p>Nenhuma mensagem agendada</p>
            <button className="message-schedule-add-button message-schedule-add-button-empty" onClick={() => showDrawer(null)}>
              <Icons.Plus />
              Agendar primeira mensagem
            </button>
          </div>
        ) : (
          <>
            <div className="ms-filters">
              <input
                type="text"
                className="ms-filter-search"
                placeholder="Buscar por título, destinatário ou descrição..."
                value={searchFilter}
                onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(0); }}
              />
            </div>
            {filteredMessages.length > 0 ? (
            <div className="message-schedule-list-container">
              <table className="message-schedule-list-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Destinatário</th>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Anexos</th>
                    <th className="ms-list-actions-col">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMessages.map((message) => {
                    const { images, audio, files } = getAttachmentCounts(message);
                    return (
                      <tr key={message.id} className={confirmDeleteId === message.id ? 'ms-list-row-deleting' : ''}>
                        {confirmDeleteId === message.id ? (
                          <td colSpan={6} className="ms-delete-confirm-cell">
                            <div className="ms-delete-confirm-inline">
                              <div>
                                <h4>Deseja mesmo excluir?</h4>
                                <p>Essa ação é irreversível.</p>
                              </div>
                              <div className="confirm-actions">
                                <button className="btn btn-secondary" onClick={() => setConfirmDeleteId(null)}>Não</button>
                                <button className="btn btn-danger" onClick={() => message.id && handleDelete(message.id)}>Sim</button>
                              </div>
                            </div>
                          </td>
                        ) : (
                          <>
                            <td><span className="ms-list-title">{message.title}</span></td>
                            <td><span className="ms-list-recipient">{message.contact?.name || '—'}</span></td>
                            <td>
                              <span className="ms-list-date">
                                <Icons.Calendar />
                                {message.date ? dayjs(message.date).tz('America/Sao_Paulo').format('DD/MM/YYYY HH:mm') : '—'}
                              </span>
                            </td>
                            <td><span className="ms-list-desc">{message.description || '—'}</span></td>
                            <td>
                              <div className="ms-list-attachments">
                                {images > 0 && <span className="ms-attach-count"><Icons.Picture /> {images}</span>}
                                {audio > 0 && <span className="ms-attach-count"><Icons.Mic /> {audio}</span>}
                                {files > 0 && <span className="ms-attach-count"><Icons.Paperclip /> {files}</span>}
                                {images === 0 && audio === 0 && files === 0 && <span className="ms-attach-none">—</span>}
                              </div>
                            </td>
                            <td className="ms-list-actions-cell">
                              <div className="message-schedule-card-actions">
                                <button className="message-schedule-action-button edit" onClick={() => showDrawer(message)} title="Editar">
                                  <Icons.Edit />
                                </button>
                                <button className="message-schedule-action-button delete" onClick={() => message.id && setConfirmDeleteId(message.id)} title="Excluir">
                                  <Icons.Delete />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : (
              <div className="ms-empty-filtered">
                <p>Nenhuma mensagem encontrada com os filtros aplicados</p>
              </div>
            )}
            {filteredMessages.length > 0 && totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                >
                  <Icons.ChevronLeft />
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {currentPage + 1} de {totalPages} ({filteredMessages.length} resultado{filteredMessages.length !== 1 ? 's' : ''})
                </span>
                <button
                  className="pagination-button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage >= totalPages - 1}
                >
                  Próxima
                  <Icons.ChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {renderDrawer()}

      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  );
};

export default MessageScheduleScreen;
